import { Injectable } from '@angular/core';
import { HttpClient, HttpEventType } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { ApiService } from '../common';
import { API_ENDPOINT } from '@core/configs';
import { ContentCategory, ContentItem } from '@pages/caremoni-app-tv/caremoni-app-tv.model';

@Injectable({
  providedIn: 'root'
})
export class MenuTvService {

  constructor(
    private readonly http: ApiService,
    private readonly httpClient: HttpClient
  ) { }

  // ─── Shared cache ──────────────────────────────────────────────────────────

  private _cache: { items: ContentItem[]; categories: ContentCategory[] } | null = null;

  /** Fetch cả items + categories song song, cache lại để dùng chung giữa các page/dialog. */
  async loadAll(): Promise<{ items: ContentItem[]; categories: ContentCategory[] }> {
    if (this._cache) return this._cache;
    const [items, categories] = await Promise.all([
      this.getAllItems(),
      this.getAllCategories()
    ]);
    this._cache = { items, categories };
    return this._cache;
  }

  /** Gọi sau mỗi mutation để page tiếp theo load lại fresh data. */
  invalidateCache() {
    this._cache = null;
  }

  // ─── Items ─────────────────────────────────────────────────────────────────

  async getAllItems(): Promise<ContentItem[]> {
    const res: any = await firstValueFrom(this.http.get(API_ENDPOINT.authConsole.menuTvItems));
    if (res?.result?.result?.success && Array.isArray(res.result.result.data)) return res.result.result.data;
    return [];
  }

  createItem(item: ContentItem): Observable<any> {
    return this.http.post(API_ENDPOINT.authConsole.menuTvItems, item);
  }

  updateItem(itemId: string, item: ContentItem): Observable<any> {
    return this.http.put(`${API_ENDPOINT.authConsole.menuTvItems}/${itemId}`, item);
  }

  deleteItem(itemId: string): Observable<any> {
    return this.http.delete(`${API_ENDPOINT.authConsole.menuTvItems}/${itemId}`, null);
  }

  // ─── Categories ────────────────────────────────────────────────────────────

  async getAllCategories(): Promise<ContentCategory[]> {
    const res: any = await firstValueFrom(this.http.get(API_ENDPOINT.authConsole.menuTvCategories));
    if (res?.result?.result?.success && Array.isArray(res.result.result.data)) return res.result.result.data;
    return [];
  }

  createCategory(category: ContentCategory): Observable<any> {
    return this.http.post(API_ENDPOINT.authConsole.menuTvCategories, category);
  }

  updateCategory(categoryId: string, category: ContentCategory): Observable<any> {
    return this.http.put(`${API_ENDPOINT.authConsole.menuTvCategories}/${categoryId}`, category);
  }

  deleteCategory(categoryId: string): Observable<any> {
    return this.http.delete(`${API_ENDPOINT.authConsole.menuTvCategories}/${categoryId}`, null);
  }

  reorderCategories(orders: { categoryId: string; sortNo: number }[]): Observable<any> {
    return this.http.put(API_ENDPOINT.authConsole.menuTvCategoriesReorder, { orders });
  }

  // ─── File (S3 media) ───────────────────────────────────────────────────────

  deleteMediaFile(path: string): Observable<any> {
    return this.httpClient.delete(API_ENDPOINT.authConsole.deleteMediaFile, { params: { path } });
  }

  async uploadFile(file: File, itemId?: string, fileRole?: 'url' | 'thumbnail', onProgress?: (percent: number) => void): Promise<{ fileUrl: string; itemId: string }> {
    const res: any = await firstValueFrom(
      this.http.post(API_ENDPOINT.authConsole.uploadFileToS3, {
        fileName: file.name,
        fileType: file.type,
        itemId: itemId ?? null,
        fileRole: fileRole ?? 'url'
      })
    );

    const uploadUrl = res.result.uploadUrl;
    const fileUrl = res.result.fileUrl;
    const returnedItemId = res.result.itemId;

    await new Promise<void>((resolve, reject) => {
      this.http.request('PUT', uploadUrl, file, { reportProgress: true, responseType: 'text' })
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress && onProgress) {
              onProgress(Math.round((event.loaded / (event.total ?? 1)) * 100));
            }
            if (event.type === HttpEventType.Response) {
              onProgress?.(100);
              resolve();
            }
          },
          error: reject
        });
    });

    return { fileUrl, itemId: returnedItemId };
  }
}
